import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { activityLogService, ActivityLog, ActivityType } from '../services/activityLogService';
import { formatDateTime } from '../utils/dateUtils';

const activityTypeOptions: { value: ActivityType | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'login_success', label: 'Login Success' },
  { value: 'login_failed', label: 'Login Failed' },
  { value: 'logout', label: 'Logout' },
  { value: 'patient_created', label: 'Patient Created' },
  { value: 'patient_updated', label: 'Patient Updated' },
  { value: 'patient_finished', label: 'Patient Finished' },
  { value: 'patient_revived', label: 'Patient Revived' },
  { value: 'meeting_created', label: 'Meeting Created' },
  { value: 'meeting_updated', label: 'Meeting Updated' },
  { value: 'meeting_status_changed', label: 'Meeting Status Changed' },
  { value: 'meeting_text_created', label: 'Meeting Text Created' },
  { value: 'meeting_text_updated', label: 'Meeting Text Updated' },
  { value: 'meeting_text_deleted', label: 'Meeting Text Deleted' },
];

const getActivityTypeColor = (type: ActivityType): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (type === 'login_success') return 'success';
  if (type === 'login_failed') return 'error';
  if (type.includes('created')) return 'success';
  if (type.includes('updated')) return 'info';
  if (type.includes('deleted')) return 'error';
  if (type.includes('finished')) return 'warning';
  return 'default';
};

const getActivityTypeLabel = (type: ActivityType): string => {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ActivityLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activityType, setActivityType] = useState<ActivityType | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await activityLogService.getActivityLogs({
          page: page + 1,
          page_size: rowsPerPage,
          search: search || undefined,
          activity_type: activityType || undefined,
          sort_by: 'created_at',
          sort_order: 'desc',
        });
        setLogs(response.items);
        setTotal(response.total);
      } catch (err: unknown) {
        const error = err as Error;
        setError(error.message || 'Failed to fetch activity logs');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [page, rowsPerPage, search, activityType]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Activity Logs
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={activityType}
              label="Activity Type"
              onChange={(e) => {
                setActivityType(e.target.value as ActivityType | '');
                setPage(0);
              }}
            >
              {activityTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Activity Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Meeting</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No activity logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDateTime(log.created_at)}</TableCell>
                  <TableCell>{log.user_name || `User #${log.user_id}`}</TableCell>
                  <TableCell>
                    <Chip
                      label={getActivityTypeLabel(log.activity_type)}
                      color={getActivityTypeColor(log.activity_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{log.description}</TableCell>
                  <TableCell>{log.patient_name || (log.patient_id ? `Patient #${log.patient_id}` : '-')}</TableCell>
                  <TableCell>{log.meeting_title || (log.meeting_id ? `Meeting #${log.meeting_id}` : '-')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Container>
  );
};

export default ActivityLogsPage;
